// 1. React and built-in hooks
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';

// 2. Third-party libraries
import * as Popover from '@radix-ui/react-popover';
import { TooltipContent } from '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

// 3. Local imports
import { Tooltip, TooltipProvider, TooltipTrigger } from '../ui/Tooltip';
import {
  updateChatHistory,
  fetchHistoryFromBlob,
  validateStringForXSS,
  trackUserClicks,
  getUserEnterpriseId,
  getValidToken,
} from '../../utils';
import { APIContext } from '../../contexts/APIContext';
import { SessionContext } from '../../contexts/SessionContext';
import { ChatHistoryItem, ChatHistoryItems } from '../../types';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { useToast } from './toast/UseToast';

interface AccordionContentProps extends ChatHistoryItem {
  setShowDeleteHistoryModal: (show: boolean) => void;
  setSelectedChatHistoryItem: (indexes: any) => void;
}

const AccordionContent: React.FC<AccordionContentProps> = ({
  ConfigurationName,
  blobURL,
  displayName,
  fileName,
  genArcPromptAnalyticsUID,
  promptName,
  setShowDeleteHistoryModal,
  setSelectedChatHistoryItem,
}) => {
  const [renameChat, setRenameChat] = useState<boolean>(false);
  const [visibleMenuButton, setVisibleMenuButton] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [currentChat, setCurrentChat] = useState<string>((displayName ?? '').slice(0, 50));
  const { setIsUpdatingChatHistory } = useContext(SessionContext);
  const {
    chatSessionId,
    websocketConnectionRef: { current: websocket },
    setSelectedHistoryPromptItem,
    userDetails,
    exportStoryInProgress,
    setExportStoryInProgress,
    setIsChatHistorySelected,
    WBUId,
    AppName,
    accounts
  } = useContext(APIContext);
  const { setIsLikeActive, setIsDislikeActive, activeChatUID, setActiveChatUID, setResponseDetailsSources } =
    useContext(ChatbotContext);
  const { toast } = useToast();

  // Limit the display name to 50 characters
  displayName = (displayName ?? '').slice(0, 50);

  const ref = useRef<HTMLDivElement>(null);

  const handleModal = useCallback(() => {
    setSelectedChatHistoryItem({
      blobURL: blobURL,
      displayName: displayName,
      fileName: fileName,
      genArcPromptAnalyticsUID: genArcPromptAnalyticsUID,
      promptName: promptName,
    });
    setShowDeleteHistoryModal(true);
  }, [
    setSelectedChatHistoryItem,
    setShowDeleteHistoryModal,
    blobURL,
    displayName,
    fileName,
    genArcPromptAnalyticsUID,
    promptName,
  ]);

  const handleMenuClick = useCallback(() => {
    setVisibleMenuButton(true);
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  const handleInteractOutside = useCallback((e: any) => {
    setVisibleMenuButton(false);
    if (!e.target.classList.contains('meat-ball-menu') && !e.target.classList.contains('IconButton')) {
      setIsMenuOpen(false);
    }
  }, []);

  const handleRenameChat = useCallback(() => {
    setRenameChat(true);
    setIsMenuOpen(false);
  }, []);

  const validateInput = async (str: any) => {
    const token = await getValidToken();
    const sanitizedStr = validateStringForXSS(str);
    const displayName = sanitizedStr === '' ? 'Untitled Conversation' : sanitizedStr;
    setCurrentChat(displayName);
    updateChatHistory(accounts, websocket, chatSessionId, displayName, false, genArcPromptAnalyticsUID, token);
  };

  const handleAcceptRenameClick = () => {
    setIsUpdatingChatHistory({ state: true, action: 'rename' });
    setRenameChat(false);
    setVisibleMenuButton(false);
    validateInput(currentChat);
  };

  const handleDiscardRenameClick = useCallback(() => {
    setCurrentChat(displayName);
    setRenameChat(false);
    setVisibleMenuButton(false);
  }, [displayName]);

  const handleChatContentClick = async (promptName: string) => {
    const token = await getValidToken();
    setActiveChatUID(genArcPromptAnalyticsUID);
    // Reset the response details sources if selected from chat history
    setResponseDetailsSources({ showCitation: false, sourceList: [] });
    setIsUpdatingChatHistory({ state: true, action: '' });
    setSelectedHistoryPromptItem((state: any) => ({ promptName: promptName }));
    // checking the value of `ConfigurationName` and setting the states of `isLikeActive` and `isDislikeActive` which reflects in ScoringWidget
    setIsLikeActive(ConfigurationName === 'Like');
    setIsDislikeActive(ConfigurationName === 'DisLike');
    fetchHistoryFromBlob(accounts, websocket, fileName, genArcPromptAnalyticsUID, token);
    setIsChatHistorySelected(true);
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Filter out invalid '< and >' characters
    const value = e.target.value.replace(/[<>]/g, '');
    setCurrentChat(value);
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handleDiscardRenameClick();
      }
    },
    [handleDiscardRenameClick]
  );

  useEffect(() => {
    if (renameChat) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [renameChat, handleClickOutside]);

  //Function to generate request obj for chathistory download
  const buildGenerateChatHistoryRequest = async () => {
    const token = await getValidToken();
    return {
      fileName: displayName,
      GenArcPromptAnalyticsUId: genArcPromptAnalyticsUID,
      userId: localStorage.getItem('gamma-userid') || crypto.randomUUID(),
      prompt: promptName,
      FileType: 'PPTX',
      function: 'download_chat_history',
      displayname: displayName,
      blobpath: fileName,
      EnterpriseId: getUserEnterpriseId(accounts),
      SessionUId: chatSessionId,
       authorization: `Bearer ${token}`,
    };
  };

  const DownloadChatHistoryToaster = async () => {
   const token = await getValidToken();
    toast({
      status: 'loading',
      description: 'Chat history is downloading...',
    });
    trackUserClicks(websocket, 'Download ChatHistory', userDetails, chatSessionId, WBUId, AppName, accounts,token);
    handleDownloadChatHistory();
  };

  const handleDownloadChatHistory = async () => {
    // api call
    const request = await buildGenerateChatHistoryRequest();
    console.log('reqObj for download_chat_history', request);
    // Send the request to the backend - websocket will never be null here
    websocket!.send('SendMessage', request);
  };
  // useEffect to watch for the exportStoryInProgress to be set
  useEffect(() => {
    if (exportStoryInProgress) {
      // Reenable the generate button after 15 seconds in case the BE doesn't respond or call fails
      setTimeout(() => {
        setExportStoryInProgress(false);
      }, 15000);
    }
  }, [exportStoryInProgress]);

  return (
    <div
      className={`history-title h-[42px] pl-3 flex justify-between items-center rounded ${
        renameChat && '!bg-[#F3E0FF] !text-gamma-standard-purple !font-normal'
      } ${
        (visibleMenuButton || activeChatUID === genArcPromptAnalyticsUID) &&
        'bg-white text-gamma-standard-purple font-medium'
      }`}
      ref={ref}
    >
      {renameChat ? (
        <input
          type="text"
          value={currentChat}
          onChange={handleInputChange}
          className="rename-input bg-[#F3E0FF] outline-none"
          style={{ caretColor: '#000000', width: '180px' }}
          autoFocus
          maxLength={50}
        />
      ) : (
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <Tooltip>
            <TooltipTrigger>
              <div className="w-[224px] py-4" onClick={() => handleChatContentClick(promptName)}>
                <span className="add-ellipsis-line-one text-start">{displayName}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-[#ffffff] rounded max-w-[248px] drop-shadow-[0_0px_0px_rgba(0,0,0,0.12)] p-2"
              sideOffset={0}
              align={'center'}
              alignOffset={0}
            >
              <p className="tooltip-content text-black text-xs !font-normal">{displayName}</p>
              <TooltipPrimitive.Arrow className="" width={18} height={12} style={{ fill: '#ffffff' }} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {renameChat ? (
        <div className="flex items-center gap-3 mr-3">
          <img
            className="cursor-pointer"
            src="/icons/icon-tick.svg"
            alt="tick-icon"
            onClick={handleAcceptRenameClick}
          />
          <img
            className="h-[14px] cursor-pointer"
            src="/icons/Close.svg"
            alt="close-icon"
            onClick={handleDiscardRenameClick}
          />
        </div>
      ) : (
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              className="IconButton h-[32px] min-w-[32px] flex justify-center items-center rounded-[50%] cursor-pointer"
              aria-label="Update dimensions"
              onClick={handleMenuClick}
            >
              <img
                className={`meat-ball-menu cursor-pointer hidden ${
                  (visibleMenuButton || activeChatUID === genArcPromptAnalyticsUID) && '!block'
                }`}
                src="/icons/icon-meatball-menu.svg"
                alt="icon-meatball-menu"
              />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="PopoverContent z-[100] bg-white shadow-[1px_1px_10px_rgba(0,0,0,0.10)] min-w-[130px] text-[14px]"
              side={'bottom'}
              sideOffset={0}
              align={'end'}
              alignOffset={-10}
              onInteractOutside={(e) => handleInteractOutside(e)}
            >
              <ul className="list-none">
                <li
                  className="px-3 py-[13px] cursor-pointer hover:text-gamma-standard-purple"
                  onClick={handleRenameChat}
                >
                  Rename
                </li>
                <li
                  className="px-3 py-[13px] cursor-pointer hover:text-gamma-standard-purple"
                  onClick={DownloadChatHistoryToaster}
                >
                  Download
                </li>
                <li className="px-3 py-[13px] cursor-pointer hover:text-gamma-standard-purple" onClick={handleModal}>
                  Delete
                </li>
              </ul>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
};

interface AccordionItemProps extends ChatHistoryItems {
  setShowDeleteHistoryModal: (show: boolean) => void;
  setSelectedChatHistoryItem: (show: any) => void;
  showAllAccordionContent: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  chatHistoryItems,
  setShowDeleteHistoryModal,
  setSelectedChatHistoryItem,
  showAllAccordionContent,
}) => {
  const [showAccordionContent, setShowAccordionContent] = useState<boolean>(showAllAccordionContent);

  useEffect(() => {
    setShowAccordionContent(showAllAccordionContent);
  }, [showAllAccordionContent]);

  const toggleAccordion = useCallback(() => {
    setShowAccordionContent((prevState) => !prevState);
  }, []);

  return (
    <div className="accordion-item">
      <div className="flex justify-between pb-4 pr-3 cursor-pointer" onClick={toggleAccordion}>
        <span>{title}</span>
        <img
          className={`w-3 cursor-pointer transition-all duration-300 ease-[ease] {
            ${showAccordionContent ? 'rotate-180 pink-filter-svg' : ''}`}
          src="/icons/Down.svg"
          alt="arrow"
        />
      </div>
      <div
        className={`flex flex-col gap-2 accordion-content transition-all duration-500 ease-[linear] ${
          showAccordionContent
            ? 'open h-auto pb-4 overflow-visible'
            : 'h-0 pb-0 overflow-hidden transform -translate-y-8 opacity-0'
        }`}
      >
        {chatHistoryItems ? (
          chatHistoryItems.map((content: ChatHistoryItem) => (
            <AccordionContent
              key={content.genArcPromptAnalyticsUID}
              {...content}
              setShowDeleteHistoryModal={setShowDeleteHistoryModal}
              setSelectedChatHistoryItem={setSelectedChatHistoryItem}
            />
          ))
        ) : (
          <div className="h-[42px] pl-3 flex items-center">No chat history available</div>
        )}
      </div>
    </div>
  );
};

interface AccordionProps {
  accordionItems: ChatHistoryItems[];
  setShowDeleteHistoryModal: (showDeleteHistoryModal: any) => void;
  setSelectedChatHistoryItem: (indexes: any) => void;
  showAllAccordionContent: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
  accordionItems,
  setShowDeleteHistoryModal,
  setSelectedChatHistoryItem,
  showAllAccordionContent,
}) => {
  return (
    <div className="accordion">
      {accordionItems.map((item, index) => (
        <AccordionItem
          key={item.title}
          {...item}
          setShowDeleteHistoryModal={setShowDeleteHistoryModal}
          setSelectedChatHistoryItem={setSelectedChatHistoryItem}
          showAllAccordionContent={showAllAccordionContent}
        />
      ))}
    </div>
  );
};
